import './index.scss'

import { TextareaHTMLAttributes, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import { useAuth } from '@auth/AuthProvider'
import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import PageHeader from '@components/PageHeader'
import SongSearchModal from '@components/SharedModal/SongSearchModal'
import SongContextMenu from '@components/SongContextMenu'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import ServicesController from '@data/controller/ServicesController'
import useService from '@hooks/useService'
import { useSeriesSummary } from '@hooks/useSeries'
import { ChurchSeries } from '@interfaces/series.interface'
import SeriesPickerModal from '../SeriesPickerModal'
import DeleteServiceModal from './DeleteServiceModal'
import { uploadLibraryThumbnail } from '@utility/libraryThumbnail'
import { isoTimestamp } from '@utility/date'
import { localizedValue } from '@utility/localizedText'
import DateButton from '../DateButton'
import {
  BIBLE_BOOKS,
  BibleBook,
  BibleVerse,
  ChurchService,
  ChurchServiceAttributesBody,
  ChurchServiceSongUpdate,
  ChurchServiceUpdate,
  MAX_SERVICE_MEDIA,
  MEDIA_KINDS,
  ServiceMedia,
  ServiceMediaKind,
  ServiceMediaOrigin
} from '@interfaces/service.interface'
import Song from '@interfaces/song.interface'

import trashIcon from 'url:@assets/icons/trash.svg';
import plusIcon from 'url:@assets/icons/plusNoCircle.svg';
import songPlaceholder from 'url:@assets/images/SongPlaceholder.png';

// Matches the context menu's exit animation.
const MENU_EXIT_DURATION_MS = 140;

// Matches save-bar-out in index.scss.
const SAVE_BAR_EXIT_MS = 200;

// What the services list hands over, so the page opens instantly instead of
// waiting on a request.
export type RoutedService = {
  service?: ChurchService;
  // Set right after a service is created, so its page can say so.
  justCreated?: boolean;
  // Where Back goes, when the service was opened from somewhere other than
  // the list it's filed in, such as a series.
  backLocation?: string;
};

// Where a new service is written, starting at the list's campus and category.
export function newServicePath(campusID: string | null, categoryID: string | null) {
  const params = new URLSearchParams();

  if (campusID) params.set('campus', campusID);
  if (categoryID) params.set('category', categoryID);

  const query = params.toString();

  return query ? `/services/new?${query}` : '/services/new';
}

// A blank service for the editor to start a new one from. It has no ID until
// Sanctuary creates it.
function emptyService(campusID: string, categoryID: string | null): ChurchService {
  return {
    id: '',
    churchID: '',
    attributes: {
      campusID,
      categoryID,
      seriesID: null,
      songs: [],
      title: '',
      description: null,
      discussionQuestions: null,
      thumbnailURL: null,
      thumbnailBlurHash: null,
      videoURL: null,
      media: [],
      verses: [],
      timestamp: '',
      additionalTimestamps: []
    }
  };
}

// The editor, starting blank. Saving creates the service and moves to its page.
export function NewServicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { campuses, categories } = useChurchDataProvider();

  // Only a campus and category that still exist are carried over.
  const requestedCampusID = searchParams.get('campus');
  const requestedCategoryID = searchParams.get('category');
  // With only one campus, it's the one: the Services tabs use it without
  // putting it in the URL.
  const campusID = campuses.some((campus) => campus.id === requestedCampusID) ?
    requestedCampusID :
    (campuses.length === 1 ? campuses[0].id : null);
  const categoryID = categories.some((category) => category.id === requestedCategoryID) ? requestedCategoryID : null;

  // Made once, so the editor's starting point doesn't shift under it.
  const [template] = useState(() => emptyService(campusID ?? '', categoryID));

  return <ServiceEditor
    isNew
    service={template}
    backLocation={servicesListPath(campusID, categoryID)}
    onSaved={(created) => {
      const state: RoutedService = { service: created, justCreated: true };
      navigate(servicePagePath(created), { replace: true, state });
    }} />
}

// A service's page. Sanctuary fetches a service by ID alone, so the URL
// doesn't need to say where it's filed.
export function servicePagePath(service: ChurchService) {
  return `/services/${service.id}`;
}

export function servicesListPath(campusID: string | null, categoryID: string | null) {
  const params = new URLSearchParams();

  if (campusID) params.set('campus', campusID);
  if (categoryID) params.set('category', categoryID);

  const query = params.toString();

  return query ? `/services?${query}` : '/services';
}

export default function ServiceDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { serviceID } = useParams();

  const routedState = (location.state as RoutedService | null) ?? null;
  const routedService = routedState?.service ?? null;
  const hasRoutedService = routedService !== null && routedService.id === serviceID;

  // Only needed when the page was reloaded or opened from a link.
  const { service: fetchedService, notFound, error, refetch } = useService(
    hasRoutedService ? null : serviceID ?? null
  );

  // Kept here once saved, so the editor carries on from what the server stored.
  const [savedService, setSavedService] = useState<ChurchService | null>(null);

  const service = savedService ?? (hasRoutedService ? routedService : fetchedService);

  if (!serviceID) {
    return <Navigate to='/services' replace />
  }

  if (service) {
    return <ServiceEditor
      key={service.id}
      service={service}
      justCreated={hasRoutedService && routedState?.justCreated === true}
      // Back returns to wherever the service was opened from, or else the
      // list it's filed in now, which follows it if a save moved it.
      backLocation={(hasRoutedService ? routedState?.backLocation : undefined) ??
        servicesListPath(service.attributes.campusID, service.attributes.categoryID)}
      onSaved={setSavedService} />
  }

  if (notFound) {
    return <div className='service-details-page'>
      <ErrorView
        title='Service Not Found'
        error="This service doesn't exist, or it's been deleted."
        buttonText='Back to Services'
        buttonAction={() => navigate('/services')}
        fullHeight />
    </div>
  }

  if (error) {
    return <div className='service-details-page'>
      <ErrorView error={error} buttonText='Retry' buttonAction={() => void refetch()} fullHeight />
    </div>
  }

  return <div className='service-details-page'>
    <div className='service-state'>
      <LoadingIndicator fullScreen={false} delayMs={250} />
    </div>
  </div>
}

// The editable copy of a service. Text fields hold '' for "none" while being
// edited, and go back to null when saved.
type ServiceDraft = {
  title: string;
  description: string;
  discussionQuestions: string;
  media: MediaDraft[];
  thumbnailURL: string;
  // Belongs to thumbnailURL: set and cleared together, so it never describes
  // a different image.
  thumbnailBlurHash: string;
  timestamp: Date | null;
  campusID: string;
  categoryID: string;
  // '' for none.
  seriesID: string;
  songs: SongDraft[];
  verses: VerseDraft[];
};

// `key` tells rows apart, since a song just picked has no ID yet.
type SongDraft = {
  key: number;
  song: ChurchServiceSongUpdate;
};

// One media row. `platform` is '' until one's chosen; `origin` rides along
// untouched, so an imported entry stays recognisable to its import.
type MediaDraft = {
  key: number;
  platform: string;
  kind: ServiceMediaKind;
  url: string;
  origin: ServiceMediaOrigin | null;
};

// Chapter and verse stay strings while typed, so a field can be cleared
// without snapping back to a number.
type VerseDraft = {
  key: number;
  book: BibleBook;
  chapter: string;
  verse: string;
  version: string;
};

let nextVerseKey = 0;
let nextSongKey = 0;
let nextMediaKey = 0;

// What Sanctuary keeps unique: one entry per kind and platform. Also the
// value of each option in a row's platform dropdown, which picks both at once.
function mediaSlot(kind: ServiceMediaKind, platform: string) {
  return `${kind}|${platform}`;
}

function parseMediaSlot(slot: string): { kind: ServiceMediaKind; platform: string } {
  const [kind, ...platform] = slot.split('|');

  return { kind: kind === 'audio' ? 'audio' : 'video', platform: platform.join('|') };
}

// Whether the dropdown offers this kind and platform, as opposed to one this
// portal doesn't know, which is then shown on its own so it isn't lost.
function isListedMediaSlot(kind: ServiceMediaKind, platform: string) {
  return MEDIA_KINDS.some((group) => group.id === kind && group.platforms.some((option) => option.id === platform));
}

// A new row on the first kind and platform, in dropdown order, that no row
// has yet, so usually only the link is left to fill in.
function newMediaDraft(existing: MediaDraft[]): MediaDraft {
  const taken = new Set(existing.map((entry) => mediaSlot(entry.kind, entry.platform)));

  for (const group of MEDIA_KINDS) {
    const platform = group.platforms.find((option) => !taken.has(mediaSlot(group.id, option.id)));

    if (platform) {
      return { key: nextMediaKey++, platform: platform.id, kind: group.id, url: '', origin: null };
    }
  }

  return { key: nextMediaKey++, platform: '', kind: 'video', url: '', origin: null };
}

// Whether a link is to YouTube, the one platform the old single `videoURL`
// maps onto without guessing.
function isYouTubeURL(value: string) {
  try {
    const host = new URL(value).hostname.toLowerCase();
    return host === 'youtu.be' || host === 'youtube.com' || host.endsWith('.youtube.com');
  } catch {
    return false;
  }
}

// A service's media as editor rows. One saved before media existed, with just
// a YouTube `videoURL`, opens with that as its YouTube row, so it carries over
// on the next save. Any other old link is left alone rather than guessed at.
function mediaDraftsFrom(service: ChurchService): MediaDraft[] {
  const media = service.attributes.media ?? [];

  if (media.length > 0) {
    return media.map((entry) => ({
      key: nextMediaKey++,
      platform: entry.platform,
      kind: entry.kind,
      url: entry.url,
      origin: entry.origin ?? null
    }));
  }

  const videoURL = service.attributes.videoURL;

  if (videoURL && isYouTubeURL(videoURL)) {
    return [{ key: nextMediaKey++, platform: 'youTube', kind: 'video', url: videoURL, origin: null }];
  }

  return [];
}

// Whether the service still has an old `videoURL` that no media row stands
// for, which then has to be kept as it is.
function hasUnmigratedVideoURL(service: ChurchService) {
  const videoURL = service.attributes.videoURL;

  return (service.attributes.media ?? []).length === 0 && Boolean(videoURL) && !isYouTubeURL(videoURL!);
}

function draftFrom(service: ChurchService): ServiceDraft {
  const attributes = service.attributes;
  const timestamp = new Date(attributes.timestamp);

  return {
    title: attributes.title ?? '',
    description: attributes.description ?? '',
    discussionQuestions: attributes.discussionQuestions ?? '',
    media: mediaDraftsFrom(service),
    thumbnailURL: attributes.thumbnailURL ?? '',
    thumbnailBlurHash: attributes.thumbnailBlurHash ?? '',
    timestamp: Number.isNaN(timestamp.getTime()) ? null : timestamp,
    campusID: attributes.campusID,
    categoryID: attributes.categoryID ?? '',
    seriesID: attributes.seriesID ?? '',
    songs: (attributes.songs ?? []).map((song) => ({
      key: nextSongKey++,
      song: { id: song.id, globalID: song.globalID, attributes: song.attributes }
    })),
    verses: (attributes.verses ?? []).map((verse) => ({
      key: nextVerseKey++,
      book: verse.book,
      chapter: String(verse.chapter),
      verse: String(verse.verse),
      version: verse.version ?? ''
    }))
  };
}

function updateFrom(draft: ServiceDraft): ChurchServiceUpdate {
  const orNull = (value: string) => value.trim().length > 0 ? value.trim() : null;

  return {
    title: draft.title.trim(),
    description: orNull(draft.description),
    discussionQuestions: orNull(draft.discussionQuestions),
    media: draft.media.map((entry): ServiceMedia => ({
      kind: entry.kind,
      platform: entry.platform,
      url: entry.url.trim(),
      origin: entry.origin
    })),
    // Kept in step with the YouTube row, for app versions that only read the
    // old single link.
    videoURL: draft.media.find((entry) => entry.platform === 'youTube' && entry.kind === 'video')?.url.trim() || null,
    thumbnailURL: orNull(draft.thumbnailURL),
    // Only sent with a thumbnail to describe.
    thumbnailBlurHash: draft.thumbnailURL.trim() ? orNull(draft.thumbnailBlurHash) : null,
    timestamp: draft.timestamp ? isoTimestamp(draft.timestamp) : '',
    campusID: draft.campusID,
    categoryID: orNull(draft.categoryID),
    seriesID: orNull(draft.seriesID),
    songs: draft.songs.map((song) => song.song),
    verses: draft.verses.map((verse): BibleVerse => ({
      book: verse.book,
      chapter: Number(verse.chapter),
      verse: Number(verse.verse),
      version: orNull(verse.version)
    }))
  };
}

// The complete attributes the update route replaces the stored ones with:
// the editor's fields, and everything it doesn't edit carried over as-is.
function requestBodyFor(service: ChurchService, draft: ServiceDraft): ChurchServiceAttributesBody {
  const attributes = service.attributes;
  const update = updateFrom(draft);

  return {
    campusID: update.campusID,
    categoryID: update.categoryID,
    seriesID: update.seriesID,
    songs: update.songs,
    title: localizedValue(attributes.title, attributes.titleLocalizations, update.title) ?? update.title,
    description: localizedValue(attributes.description, attributes.descriptionLocalizations, update.description),
    discussionQuestions: localizedValue(
      attributes.discussionQuestions,
      attributes.discussionQuestionsLocalizations,
      update.discussionQuestions
    ),
    thumbnailURL: update.thumbnailURL,
    thumbnailBlurHash: update.thumbnailBlurHash ?? null,
    media: update.media,
    // An old non-YouTube link with nothing standing in for it is kept, so
    // opening and saving a service never loses it.
    videoURL: update.videoURL ?? (hasUnmigratedVideoURL(service) ? attributes.videoURL : null),
    verses: update.verses,
    timestamp: update.timestamp,
    additionalTimestamps: attributes.additionalTimestamps ?? []
  };
}

// Sanctuary's refusal for a thumbnail hosted outside Salt, reworded for the
// person who hit it.
function friendlySaveError(error: string) {
  if (error.includes('Assets must be uploaded through the asset upload target')) {
    return "This service's thumbnail is hosted outside Salt, so it can't be saved as is. Upload the thumbnail again, or remove it, then save.";
  }

  return error;
}

// Songs are stored with the IDs they're sent with, so a new one needs its own.
function newSongID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function isWebURL(value: string) {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

function isSecureURL(value: string) {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' && url.hostname.length > 0;
  } catch {
    return false;
  }
}

function isPositiveWholeNumber(value: string) {
  return /^\d+$/.test(value.trim()) && Number(value) > 0;
}

// Everything the draft needs before it can be saved, as messages to show.
function problemsWith(draft: ServiceDraft) {
  const problems: string[] = [];

  if (draft.title.trim().length === 0) {
    problems.push('Add a title.');
  }

  if (!draft.timestamp) {
    problems.push('Choose when the service happens.');
  }

  if (!draft.campusID) {
    problems.push('Choose a campus.');
  }

  if (draft.media.some((entry) => !entry.platform)) {
    problems.push('Choose a platform for each media link.');
  }

  // Sanctuary only accepts HTTPS, since apps open these links directly.
  if (draft.media.some((entry) => !isSecureURL(entry.url))) {
    problems.push('Each media link needs to be a full web address, starting with https://.');
  }

  if (draft.thumbnailURL.trim() && !isWebURL(draft.thumbnailURL)) {
    problems.push('The thumbnail link needs to be a full web address, starting with https://.');
  }

  if (draft.verses.some((verse) => !isPositiveWholeNumber(verse.chapter) || !isPositiveWholeNumber(verse.verse))) {
    problems.push('Every verse needs a chapter and verse number.');
  }

  return problems;
}

// Grows with its text instead of scrolling or offering a resize handle, so its
// card fits what's written. The CSS min-height sets the starting size.
function AutoSizingTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const fit = () => {
    const textarea = ref.current;

    if (!textarea) return;

    // Collapsed first so it can shrink when text is deleted, then sized to fit.
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useLayoutEffect(fit, [props.value]);

  // A narrower card wraps the text onto more lines.
  useEffect(() => {
    window.addEventListener('resize', fit);

    return () => window.removeEventListener('resize', fit);
  }, []);

  return <textarea {...props} ref={ref} rows={1} />
}

type SongMenuState = {
  key: number;
  x: number;
  y: number;
};

function songForMenu(entry: SongDraft): Song {
  return { id: entry.song.id, globalID: entry.song.globalID, attributes: entry.song.attributes };
}

function ServiceEditor(props: {
  service: ChurchService;
  backLocation: string;
  onSaved: (service: ChurchService) => void;
  // Starting blank: saving creates the service instead of updating one.
  isNew?: boolean;
  // Opened right after creating this service.
  justCreated?: boolean;
}) {
  const isNew = props.isNew === true;
  const navigate = useNavigate();
  const auth = useAuth();
  const { campuses, categories } = useChurchDataProvider();

  const initialDraft = useMemo(() => draftFrom(props.service), [props.service]);
  const [draft, setDraft] = useState<ServiceDraft>(initialDraft);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isSongPickerOpen, setIsSongPickerOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSeriesPickerOpen, setIsSeriesPickerOpen] = useState(false);

  // Kept after the error clears, so the message stays readable while it
  // collapses away rather than vanishing first.
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      setLastError(error);
    }
  }, [error]);

  // A brief confirmation, since the save bar disappearing is easy to miss.
  const [savedNotice, setSavedNotice] = useState<'saved' | 'created' | null>(props.justCreated ? 'created' : null);

  useEffect(() => {
    if (!savedNotice) return;

    const timeout = window.setTimeout(() => setSavedNotice(null), 3000);

    return () => window.clearTimeout(timeout);
  }, [savedNotice]);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // The menu stays mounted through its exit animation, so the selected and
  // displayed states are tracked separately.
  const [selectedSongMenu, setSelectedSongMenu] = useState<SongMenuState | null>(null);
  const [displayedSongMenu, setDisplayedSongMenu] = useState<SongMenuState | null>(null);
  const [isClosingSongMenu, setIsClosingSongMenu] = useState(false);
  const songMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedSongMenu) {
      setDisplayedSongMenu(selectedSongMenu);
      setIsClosingSongMenu(false);
      return;
    }

    if (!displayedSongMenu) {
      setIsClosingSongMenu(false);
      return;
    }

    setIsClosingSongMenu(true);

    const timeout = window.setTimeout(() => {
      setDisplayedSongMenu(null);
      setIsClosingSongMenu(false);
    }, MENU_EXIT_DURATION_MS);

    return () => window.clearTimeout(timeout);
  }, [selectedSongMenu, displayedSongMenu]);

  const update = <Key extends keyof ServiceDraft>(key: Key, value: ServiceDraft[Key]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const updateMedia = (key: number, changes: Partial<MediaDraft>) => {
    setDraft((current) => ({
      ...current,
      media: current.media.map((entry) => entry.key === key ? { ...entry, ...changes } : entry)
    }));
  };

  const updateVerse = (key: number, changes: Partial<VerseDraft>) => {
    setDraft((current) => ({
      ...current,
      verses: current.verses.map((verse) => verse.key === key ? { ...verse, ...changes } : verse)
    }));
  };

  // Compared as they'd be sent, so typing a space and deleting it isn't a change.
  const hasChanges = JSON.stringify(updateFrom(draft)) !== JSON.stringify(updateFrom(initialDraft));
  const problems = problemsWith(draft);
  const canSave = hasChanges && problems.length === 0 && !isSaving && !isUploadingThumbnail;

  // A new service always offers its Create button, so the way to finish is
  // never hidden, and says what's still needed.
  const showsSaveBar = isNew || hasChanges;

  // Uploaded as soon as it's picked, so the draft only ever holds a URL, and
  // Discard puts the old image back like any other field. Its BlurHash is
  // worked out from the file alongside the upload, so it adds no wait.
  const uploadThumbnail = async (file: File) => {
    if (!auth.churchId) return;

    setIsUploadingThumbnail(true);
    setThumbnailError(null);

    try {
      const { thumbnail, error: uploadError } = await uploadLibraryThumbnail(auth.churchId, file, 'service', auth);

      if (!thumbnail) {
        setThumbnailError(uploadError ?? "The thumbnail couldn't be uploaded. Please try again.");
        return;
      }

      // Set in one update, so the draft never pairs the new image with the
      // old image's hash.
      setDraft((current) => ({ ...current, thumbnailURL: thumbnail.url, thumbnailBlurHash: thumbnail.blurHash ?? '' }));
    } finally {
      setIsUploadingThumbnail(false);
    }
  };

  // The save bar stays mounted through its exit animation, so it leaves
  // instead of vanishing when the changes are discarded or saved.
  const [isSaveBarMounted, setIsSaveBarMounted] = useState(showsSaveBar);
  const [isSaveBarLeaving, setIsSaveBarLeaving] = useState(false);

  useEffect(() => {
    if (showsSaveBar) {
      setIsSaveBarMounted(true);
      setIsSaveBarLeaving(false);
      return;
    }

    if (!isSaveBarMounted) return;

    setIsSaveBarLeaving(true);

    const timeout = window.setTimeout(() => {
      setIsSaveBarMounted(false);
      setIsSaveBarLeaving(false);
    }, SAVE_BAR_EXIT_MS);

    return () => window.clearTimeout(timeout);
  }, [showsSaveBar, isSaveBarMounted]);

  // A reload or closed tab would lose the edits without asking.
  useEffect(() => {
    if (!hasChanges) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [hasChanges]);

  const save = async () => {
    if (!auth.churchId || !canSave) return;

    setIsSaving(true);
    setError(null);

    try {
      const body = requestBodyFor(props.service, draft);
      const response = isNew ?
        await ServicesController.createService(auth.churchId, body, auth) :
        await ServicesController.updateService(auth.churchId, props.service, body, auth);
      const saved = response.body;

      if (response.error || !saved) {
        setError(friendlySaveError(response.error || `Unable to ${isNew ? 'create' : 'save'} this service right now.`));
        return;
      }

      // The new service's own page takes over from here.
      if (isNew) {
        props.onSaved(saved);
        return;
      }

      // Rebuilt from what was stored, so the editor matches the server exactly
      // and the save bar goes away.
      setDraft(draftFrom(saved));
      setSavedNotice('saved');
      props.onSaved(saved);
    } finally {
      setIsSaving(false);
    }
  };

  // The saved campus, for the header; the picker shows the draft's.
  const campusName = campuses.find((campus) => campus.id === props.service.attributes.campusID)?.attributes.name;
  const sortedCampuses = [...campuses].sort((one, two) => one.attributes.name.localeCompare(two.attributes.name));
  // A campus deleted since the service was filed isn't in the list, but it's
  // still the service's until another is chosen.
  const isUnknownCampus = draft.campusID !== '' && !campuses.some((campus) => campus.id === draft.campusID);
  const sortedCategories = [...categories].sort((one, two) => one.name.localeCompare(two.name));
  const menuSong = draft.songs.find((entry) => entry.key === displayedSongMenu?.key) ?? null;

  // The series row shows a name, so it needs the series itself: the one
  // chosen in the picker, or else the one the service was saved with, looked
  // up on its own rather than by loading a whole list.
  const [chosenSeries, setChosenSeries] = useState<ChurchSeries | null>(null);
  const savedSeries = useSeriesSummary(
    props.service.attributes.campusID || null,
    props.service.attributes.seriesID ?? null
  );

  let seriesLabel = 'No Series';

  if (draft.seriesID) {
    if (chosenSeries?.id === draft.seriesID) {
      seriesLabel = chosenSeries.attributes.name || 'Untitled Series';
    } else if (savedSeries.series?.id === draft.seriesID) {
      seriesLabel = savedSeries.series.attributes.name || 'Untitled Series';
    } else {
      // Filed at another campus, where the lookup can't see it.
      seriesLabel = savedSeries.loading ? 'Loading…' : 'Current Series';
    }
  }

  return <div className='service-details-page'>
    <PageHeader
      title={draft.title.trim() || (isNew ? 'New Service' : 'Untitled Service')}
      subtitle={isNew ? undefined : campusName}
      backLocation={props.backLocation}
      // A service that hasn't been created yet has nothing to delete; Cancel
      // leaves it instead.
      links={isNew ? undefined : [
        {
          icon: trashIcon,
          title: 'Delete',
          onClick: () => setIsDeleteModalOpen(true)
        }
      ]} />

    {draft.campusID ? <SeriesPickerModal
      isOpen={isSeriesPickerOpen}
      campusID={draft.campusID}
      categoryID={draft.categoryID || null}
      selectedSeriesID={draft.seriesID || null}
      close={() => setIsSeriesPickerOpen(false)}
      // Kept in the draft like any other edit; a series created from the
      // picker arrives here too, chosen straight away.
      onSelect={(picked) => {
        setChosenSeries(picked);
        update('seriesID', picked?.id ?? '');
        setIsSeriesPickerOpen(false);
      }} /> : null}

    {isNew ? null : <DeleteServiceModal
      isOpen={isDeleteModalOpen}
      service={props.service}
      close={() => setIsDeleteModalOpen(false)}
      // Replaced, so Back from the list doesn't return to a service that's gone.
      onDeleted={() => navigate(props.backLocation, { replace: true })} />}

    <SongSearchModal
      id='add-song-to-service-modal'
      isOpen={isSongPickerOpen}
      close={() => setIsSongPickerOpen(false)}
      onConfirm={(selection) => {
        // Kept in the draft like any other edit; nothing is sent until Save.
        update('songs', [...draft.songs, { key: nextSongKey++, song: { id: newSongID(), attributes: selection.selectedSong } }]);
      }} />

    {displayedSongMenu && menuSong ? <SongContextMenu
      song={songForMenu(menuSong)}
      menuRef={songMenuRef}
      x={displayedSongMenu.x}
      y={displayedSongMenu.y}
      isClosing={isClosingSongMenu}
      onRequestClose={() => setSelectedSongMenu(null)}
      onRemove={() => {
        setSelectedSongMenu(null);
        update('songs', draft.songs.filter((entry) => entry.key !== menuSong.key));
      }} /> : null}

    <form
      className='service-form'
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}>
      <h3>Details</h3>
      <div className='section'>
        <div className='input'>
          <input
            type='text'
            placeholder='Title'
            aria-label='Title'
            value={draft.title}
            disabled={isSaving}
            onChange={(e) => update('title', e.target.value)} />
        </div>

        <div className='separator' />

        <div className='input'>
          <AutoSizingTextarea
            placeholder='Description'
            aria-label='Description'
            value={draft.description}
            disabled={isSaving}
            onChange={(e) => update('description', e.target.value)} />
        </div>
      </div>

      <h3>Discussion Questions</h3>
      <div className='section'>
        <div className='input'>
          <AutoSizingTextarea
            placeholder='Discussion Questions'
            aria-label='Discussion questions'
            className='tall'
            value={draft.discussionQuestions}
            disabled={isSaving}
            onChange={(e) => update('discussionQuestions', e.target.value)} />
        </div>
      </div>
      <p className='footnote'>Discussion Questions are shown alongside the service in Launchpad and in the Salt app.</p>

      <h3>Schedule</h3>
      <div className='section'>
        <div className='input labeled'>
          <label>Date</label>
          {/* Its own box: opening the picker adds an element beside the input,
              which would otherwise take a gap in this row and shift it. */}
          <div className='date-picker-container'>
            <DatePicker
              customInput={<DateButton />}
              selected={draft.timestamp}
              disabled={isSaving}
              showTimeInput
              timeInputLabel='Time'
              timeFormat='p'
              dateFormat='Pp'
              onChange={(date) => update('timestamp', date)} />
          </div>
        </div>

        <div className='separator' />

        <div className='input labeled'>
          <label htmlFor='service-campus'>Campus</label>
          <select
            id='service-campus'
            value={draft.campusID}
            disabled={isSaving}
            onChange={(e) => update('campusID', e.target.value)}>
            {draft.campusID === '' ? <option value='' disabled>Choose a campus</option> : null}
            {isUnknownCampus ? <option value={draft.campusID} disabled>Unknown Campus</option> : null}
            {sortedCampuses.map((campus) => (
              <option key={campus.id} value={campus.id}>{campus.attributes.name}</option>
            ))}
          </select>
        </div>

        <div className='separator' />

        <div className='input labeled'>
          <label htmlFor='service-category'>Category</label>
          <select
            id='service-category'
            value={draft.categoryID}
            disabled={isSaving}
            onChange={(e) => update('categoryID', e.target.value)}>
            <option value=''>No Category</option>
            {sortedCategories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
        </div>

        <div className='separator' />

        <div className='input labeled'>
          <label htmlFor='service-series'>Series</label>
          {/* Opens a picker rather than a menu, so it reads as a row to open,
              not a dropdown. */}
          <button
            id='service-series'
            type='button'
            className='row-link'
            // The picker lists series from the service's campus.
            disabled={isSaving || !draft.campusID}
            onClick={() => setIsSeriesPickerOpen(true)}>
            <span>{seriesLabel}</span>
            <svg className='row-link-chevron' viewBox='0 0 8 14' aria-hidden='true'>
              <path d='M1.5 1.5l5 5.5-5 5.5' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </button>
        </div>
      </div>
      <p className='footnote'>
        A service isn't shown in Launchpad or in the Salt app until its date has passed. Series are chosen from
        the campus and category above.
      </p>

      <h3>Thumbnail</h3>
      <div className='section thumbnail-section'>
        {draft.thumbnailURL ?
          <img className='thumbnail-preview' src={draft.thumbnailURL} alt='Thumbnail preview' /> :
          <div className='thumbnail-preview empty' aria-hidden='true' />}

        <div className='thumbnail-controls'>
          <input
            ref={thumbnailInputRef}
            type='file'
            accept='image/*'
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Cleared so picking the same file again still fires.
              e.target.value = '';

              if (file) {
                void uploadThumbnail(file);
              }
            }} />

          <button
            type='button'
            className='secondary-button'
            disabled={isSaving || isUploadingThumbnail}
            onClick={() => thumbnailInputRef.current?.click()}>
            {isUploadingThumbnail ? 'Uploading...' : draft.thumbnailURL ? 'Upload New Thumbnail' : 'Upload Thumbnail'}
          </button>

          {draft.thumbnailURL && !isUploadingThumbnail ? <button
            type='button'
            className='text-button destructive'
            disabled={isSaving}
            onClick={() => setDraft((current) => ({ ...current, thumbnailURL: '', thumbnailBlurHash: '' }))}>
            Remove Thumbnail
          </button> : null}

          {thumbnailError ? <p className='thumbnail-error'>{thumbnailError}</p> : null}
        </div>
      </div>
      <p className='footnote'>A 16:9 image works best. It's shown wherever the service appears in Launchpad and in the Salt app.</p>

      <h3>Media</h3>
      <div className='section media'>
        {draft.media.map((entry, index) => {
          // Sanctuary allows each platform once per kind, so YouTube can carry
          // both a video and an audio entry but not two of either.
          const takenElsewhere = new Set(draft.media
            .filter((other) => other.key !== entry.key)
            .map((other) => mediaSlot(other.kind, other.platform)));

          return <div key={entry.key}>
            {index > 0 ? <div className='separator' /> : null}

            <div className='media-row'>
              {/* Grouped into Video and Audio, so choosing a platform also
                  says which kind of media the link is. */}
              <select
                aria-label='Platform'
                value={entry.platform ? mediaSlot(entry.kind, entry.platform) : ''}
                disabled={isSaving}
                onChange={(e) => updateMedia(entry.key, parseMediaSlot(e.target.value))}>
                {entry.platform === '' ? <option value='' disabled>Platform</option> : null}
                {/* One this portal doesn't list, kept rather than dropped. */}
                {entry.platform && !isListedMediaSlot(entry.kind, entry.platform) ? <option value={mediaSlot(entry.kind, entry.platform)}>
                  {entry.platform} ({entry.kind === 'audio' ? 'Audio' : 'Video'})
                </option> : null}
                {MEDIA_KINDS.map((group) => <optgroup key={group.id} label={group.name}>
                  {group.platforms.map((platform) => {
                    const slot = mediaSlot(group.id, platform.id);

                    return <option key={slot} value={slot} disabled={takenElsewhere.has(slot)}>
                      {platform.name}
                    </option>
                  })}
                </optgroup>)}
              </select>

              <input
                type='url'
                className='media-url'
                aria-label='Link'
                placeholder='https://'
                value={entry.url}
                disabled={isSaving}
                onChange={(e) => updateMedia(entry.key, { url: e.target.value })} />

              <button
                type='button'
                className='remove-button'
                aria-label='Remove media'
                disabled={isSaving}
                onClick={() => update('media', draft.media.filter((other) => other.key !== entry.key))}>
                <img src={trashIcon} alt='' aria-hidden='true' />
              </button>
            </div>
          </div>
        })}

        {/* Like Songs and Scripture: with no media, the card holds just the
            button. */}
        {draft.media.length > 0 ? <div className='separator' /> : null}

        {draft.media.length < MAX_SERVICE_MEDIA ? <button
          type='button'
          className='add-button'
          disabled={isSaving}
          onClick={() => update('media', [...draft.media, newMediaDraft(draft.media)])}>
          <img src={plusIcon} alt='' aria-hidden='true' />
          Add Media
        </button> : null}
      </div>
      <p className='footnote'>Links to this service's episode or video on each platform, opened in Launchpad and in the Salt app.</p>

      <h3>Songs</h3>
      <div className='section songs'>
        {draft.songs.length > 0 ? <ol>
          {draft.songs.map((entry) => {
            const isMenuOpen = displayedSongMenu?.key === entry.key;

            return <li key={entry.key}>
              <button
                type='button'
                className='song-row'
                disabled={isSaving}
                onClick={(event) => {
                  if (isMenuOpen) {
                    setSelectedSongMenu(null);
                    return;
                  }

                  setSelectedSongMenu({
                    key: entry.key,
                    x: event.clientX + window.scrollX,
                    y: event.clientY + window.scrollY
                  });
                }}>
                <img src={entry.song.attributes.artworkURL || songPlaceholder} alt='' aria-hidden='true' />

                <span className='song-text'>
                  <span className='song-title'>{entry.song.attributes.title}</span>
                  <span className='song-artist'>{entry.song.attributes.artist?.name}</span>
                </span>
              </button>
            </li>
          })}
        </ol> : null}

        {/* With no songs, the card holds just the button. */}
        {draft.songs.length > 0 ? <div className='separator' /> : null}

        <button
          type='button'
          className='add-button'
          disabled={isSaving}
          onClick={() => setIsSongPickerOpen(true)}>
          <img src={plusIcon} alt='' aria-hidden='true' />
          Add Song
        </button>
      </div>

      <h3>Scripture</h3>
      <div className='section verses'>
        {draft.verses.map((verse, index) => <div key={verse.key}>
          {index > 0 ? <div className='separator' /> : null}

          <div className='verse-row'>
            <select
              aria-label='Book'
              value={verse.book}
              disabled={isSaving}
              onChange={(e) => updateVerse(verse.key, { book: e.target.value as BibleBook })}>
              {BIBLE_BOOKS.map(([book, name]) => <option key={book} value={book}>{name}</option>)}
            </select>

            <input
              type='text'
              inputMode='numeric'
              aria-label='Chapter'
              placeholder='Ch.'
              className='number'
              value={verse.chapter}
              disabled={isSaving}
              onChange={(e) => updateVerse(verse.key, { chapter: e.target.value })} />

            <span className='colon' aria-hidden='true'>:</span>

            <input
              type='text'
              inputMode='numeric'
              aria-label='Verse'
              placeholder='Vs.'
              className='number'
              value={verse.verse}
              disabled={isSaving}
              onChange={(e) => updateVerse(verse.key, { verse: e.target.value })} />

            <input
              type='text'
              aria-label='Translation'
              placeholder='Translation (optional)'
              className='version'
              value={verse.version}
              disabled={isSaving}
              onChange={(e) => updateVerse(verse.key, { version: e.target.value })} />

            <button
              type='button'
              className='remove-button'
              aria-label='Remove verse'
              disabled={isSaving}
              onClick={() => update('verses', draft.verses.filter((candidate) => candidate.key !== verse.key))}>
              <img src={trashIcon} alt='' aria-hidden='true' />
            </button>
          </div>
        </div>)}

        {/* Like Songs: with no verses, the card holds just the button. */}
        {draft.verses.length > 0 ? <div className='separator' /> : null}

        <button
          type='button'
          className='add-button'
          disabled={isSaving}
          onClick={() => update('verses', [
            ...draft.verses,
            { key: nextVerseKey++, book: 'genesis', chapter: '', verse: '', version: '' }
          ])}>
          <img src={plusIcon} alt='' aria-hidden='true' />
          Add Verse
        </button>
      </div>
      <p className='footnote'>Launchpad and the Salt app will automatically generate BibleGateway links for the selected verse and translation.</p>

      {/* Waits for the save bar to finish leaving, so the two don't overlap. */}
      {!isSaveBarMounted && savedNotice ? <div className='save-bar saved' role='status'>
        <p className='save-bar-status'>{savedNotice === 'created' ? 'Service created.' : 'Changes saved.'}</p>
      </div> : null}

      {/* Floats over the page while there's something to save, and settles
          at the end of the form once scrolled there. A failed save explains
          itself here too, where the attempt was made. */}
      {isSaveBarMounted ? <div className={[
        'save-bar',
        error ? 'has-error' : '',
        isSaveBarLeaving ? 'is-leaving' : ''
      ].join(' ').trim()}>
        {/* Always rendered, so it can open and close smoothly instead of
            resizing the bubble in one jump. */}
        <div className={error ? 'save-bar-error is-open' : 'save-bar-error'} aria-hidden={!error}>
          <div className='save-bar-error-clip'>
            <p role={error ? 'alert' : undefined}>{error ?? lastError}</p>
          </div>
        </div>

        <div className='save-bar-row'>
          <p className='save-bar-status'>
            {problems.length > 0 ? problems[0] : isNew ? 'Ready to create.' : 'You have unsaved changes.'}
          </p>

          <button
            type='button'
            className='discard-button'
            disabled={isSaving || isSaveBarLeaving}
            onClick={() => {
              // A new service has nothing to go back to, so this leaves it.
              if (isNew) {
                navigate(props.backLocation);
                return;
              }

              setDraft(initialDraft);
              setError(null);
            }}>
            {isNew ? 'Cancel' : 'Discard'}
          </button>

          <button type='submit' className='save-button' disabled={!canSave || isSaveBarLeaving}>
            {isNew ?
              (isSaving ? 'Creating...' : 'Create Service') :
              (isSaving ? 'Saving...' : 'Save Changes')}
          </button>
        </div>
      </div> : null}
    </form>
  </div>
}
